import { notification } from "antd";
import cuid from "cuid";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_PRICER } from "utils/api/graphql/mutations/pricers";
import { PRICERS } from "utils/api/graphql/queries/pricers";
import { INITIAL_VALUES, PRICER_TYPES, SCREENS } from "utils/constants";
import ImportWizard from "./Import";
import MethodSelect from "./MethodSelect";
import View from "./View";

const Create = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const ERRORS = t("ERRORS", { returnObjects: true });
  const navigate = useNavigate();
  const [method, setMethod] = useState(null);
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);

  const [createPricer] = useMutation(CREATE_PRICER, {
    refetchQueries: [
      {
        query: PRICERS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
    onCompleted: () => {
      notification.success({
        message: t("CREATE.NOTIFICATION_SUCCESS"),
      });
      navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
    },
    onError: () => notification.error({ message: ERRORS.DATE_CONFLICT }),
  });

  const onSubmit = ({ prices, isZipCode, dates, ...values }) => {
    const [startDate, endDate] = dates;
    const transformedPrices = prices.map(
      ({
        costs,
        departments,
        zipCodes,
        socialRegime,
        commission,
        beneficiaryType,
      }) => ({
        costs: JSON.parse(costs),
        socialRegime,
        beneficiaryType,
        commission,
        departments: isEmpty(departments) ? undefined : departments,
        zipCodes: isEmpty(zipCodes) ? undefined : zipCodes,
      }),
    );

    return createPricer({
      variables: {
        data: {
          id: cuid(),
          ...values,
          type: PRICER_TYPES.DB,
          startDate: startDate.startOf("day"),
          endDate: endDate.endOf("day"),
          prices: transformedPrices,
        },
      },
    });
  };

  if (!method) {
    return (
      <MethodSelect
        onSelectManual={() => setMethod("manual")}
        onSelectImport={() => setMethod("import")}
      />
    );
  }

  if (method === "import") {
    return <ImportWizard onBack={() => setMethod(null)} />;
  }

  return (
    <View
      onSubmit={(formValues, riskVal) => onSubmit(formValues, riskVal)}
      cancel={cancel}
    />
  );
};

export default Create;
